import { TaskAdmin } from '@gleamer/types';
import { parse as parseDuration } from 'tinyduration';
import YAML from 'yaml';

type AdminLabelTaskDetails = TaskAdmin.AdminLabelTaskDetails;

/**
 * A task being written in the backoffice, and the YAML it becomes: the same document the data managers
 * attach to Notion today and `POST /api/label-tasks` reads (viewer-api parses it with SnakeYAML, then
 * Jackson). The common fields are a form; the annotation setup — observations, characterisations, layout,
 * report highlights — stays YAML, as it is nested JSON schemas the form could not do justice to.
 */
export type TaskDraft = {
  name: string;
  description: string;
  instructions: string;
  dataset: string;
  initialPassesTable: string;
  labellers: string[];
  requiredLabelers: number;
  reviewers: string[];
  casesPerReview: number;
  /** Empty: viewer-api's default. */
  logoutMinutes: string;
  reportsHidden: boolean;
  predictions: {
    enabled: boolean;
    table: string;
    minConfidence: number;
    maxConfidence: number;
    boxesColor: string;
    cleanable: boolean;
  };
  /** YAML: observations (required), characterisations, layout, reportHighlights. */
  annotation: string;
};

export const ANNOTATION_KEYS = [
  'observations',
  'characterisations',
  'layout',
  'reportHighlights',
] as const;

export const OBSERVATION_STATUSES = ['POSITIVE', 'NEGATIVE', 'UNCERTAIN'];

/** The tools the viewer draws observations with (gdata-extension/src/utils/tools). */
export const TOOLS = [
  { value: 'GleamerRectangleTool', label: 'Box' },
  { value: 'GleamerKeypointTool', label: 'Point' },
];

export const BLANK_ANNOTATION = `observations:
  - code: FINDING
    status: POSITIVE
    label: Finding
    colors: ["#a10693"]
    regions:
      - tool:
          name: GleamerRectangleTool
`;

export function emptyDraft(): TaskDraft {
  return {
    name: '',
    description: '',
    instructions: '',
    dataset: '',
    initialPassesTable: '',
    labellers: [],
    requiredLabelers: 1,
    reviewers: [],
    casesPerReview: 10,
    logoutMinutes: '',
    reportsHidden: false,
    predictions: {
      enabled: false,
      table: '',
      minConfidence: 0,
      maxConfidence: 100,
      boxesColor: '#ff0000',
      cleanable: false,
    },
    annotation: BLANK_ANNOTATION,
  };
}

/** `PT15M` → `15`; what cannot be read is left empty (the default). */
export function logoutMinutes(duration: unknown): string {
  if (typeof duration === 'number') {
    // a Duration Jackson wrote as seconds
    return duration > 0 ? String(Math.round(duration / 60)) : '';
  }
  if (typeof duration !== 'string' || !duration) {
    return '';
  }
  try {
    const d = parseDuration(duration.toUpperCase());
    const minutes =
      (d.days ?? 0) * 1440 +
      (d.hours ?? 0) * 60 +
      (d.minutes ?? 0) +
      Math.round((d.seconds ?? 0) / 60);
    return minutes > 0 ? String(minutes) : '';
  } catch {
    return '';
  }
}

/** Drops the nulls and empty objects viewer-api's JSON carries, so the YAML only says what was set. */
function clean(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(clean);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value)
      .map(([key, v]) => [key, clean(v)] as const)
      .filter(([, v]) => v !== null && v !== undefined);
    return Object.fromEntries(entries);
  }
  return value;
}

function annotationOf(source: Record<string, unknown>): string {
  const picked = Object.fromEntries(
    ANNOTATION_KEYS.filter(
      key =>
        source[key] != null &&
        !(Array.isArray(source[key]) && (source[key] as unknown[]).length === 0)
    ).map(key => [key, clean(source[key])])
  );
  return Object.keys(picked).length ? YAML.stringify(picked) : BLANK_ANNOTATION;
}

function emails(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((e): e is string => typeof e === 'string')
    : [];
}

/**
 * A draft from a task document: an existing task's details (to duplicate it) or a YAML file. Reviewers are
 * counted among the taskers by viewer-api; the draft keeps them apart.
 */
export function draftFromDocument(doc: Record<string, unknown>): TaskDraft {
  const draft = emptyDraft();
  const review = (doc.reviewConfiguration ?? {}) as Record<string, unknown>;
  const reviewers = emails(review.reviewers);
  const predictions = (doc.predictionsConfiguration ?? null) as Record<
    string,
    unknown
  > | null;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    ...draft,
    name: str(doc.name),
    description: str(doc.description),
    instructions: str(doc.instructions),
    dataset: str(doc.dataset ?? doc.bigQueryTableId),
    initialPassesTable: str(doc.bigQueryInitialPassesTableId),
    labellers: emails(doc.taskers).filter(e => !reviewers.includes(e)),
    requiredLabelers: Number(doc.requiredLabelers) || 1,
    reviewers,
    casesPerReview: Number(review.casesPerReview) || 10,
    logoutMinutes: logoutMinutes(doc.logout),
    reportsHidden: doc.reportsHidden === true,
    predictions: predictions
      ? {
          enabled: true,
          table: str(predictions.bigQueryPredictionTableId),
          minConfidence: Number(predictions.minConfidence ?? 0),
          maxConfidence: Number(predictions.maxConfidence ?? 100),
          boxesColor: str(predictions.boxesColor) || '#ff0000',
          cleanable: predictions.cleanable === true,
        }
      : draft.predictions,
    annotation: annotationOf(doc),
  };
}

/** A copy of a task, to start a new one from: everything but its name, which must be the new task's own. */
export function draftFromTask(task: AdminLabelTaskDetails): TaskDraft {
  return {
    ...draftFromDocument(task as unknown as Record<string, unknown>),
    name: '',
  };
}

export type AnnotationSummary = {
  value: Record<string, unknown>;
  observations: {
    code: string;
    status: string;
    label: string;
    colors: string[];
    tools: string[];
  }[];
  levels: string[];
  error?: string;
};

/** The annotation YAML read back: what it holds, or why it cannot be read. */
export function readAnnotation(text: string): AnnotationSummary {
  try {
    const value = YAML.parse(text) ?? {};
    if (typeof value !== 'object' || Array.isArray(value)) {
      return {
        value: {},
        observations: [],
        levels: [],
        error: 'The annotation setup must be a mapping (observations: …).',
      };
    }
    const unknown = Object.keys(value).filter(
      key => !(ANNOTATION_KEYS as readonly string[]).includes(key)
    );
    const observations = Array.isArray(value.observations)
      ? value.observations
      : [];
    return {
      value,
      observations: observations.map((o: Record<string, unknown>) => ({
        code: String(o?.code ?? ''),
        status: String(o?.status ?? ''),
        label: String(o?.label ?? ''),
        colors: emails(o?.colors),
        tools: (Array.isArray(o?.regions) ? o.regions : []).map(
          (r: { tool?: { name?: string } }) => r?.tool?.name ?? '?'
        ),
      })),
      levels: Object.keys((value.characterisations ?? {}) as object),
      error: unknown.length
        ? `Unknown key${unknown.length > 1 ? 's' : ''} here: ${unknown.join(', ')} (only ${ANNOTATION_KEYS.join(', ')}).`
        : undefined,
    };
  } catch (e) {
    return {
      value: {},
      observations: [],
      levels: [],
      error: (e as Error).message,
    };
  }
}

/** A region of an observation: the tool it is drawn with, where it applies, the questions asked about it. */
export type RegionSpec = {
  tool: { name: string; control?: string | null; configuration?: unknown };
  selector?: { modalities?: string[]; sopClassUIDs?: string[] } | null;
  characterisation?: { json?: Record<string, unknown>; ui?: unknown } | null;
  [key: string]: unknown;
};

/** An observation as the task file holds it; unknown keys are kept as they are. */
export type ObservationSpec = {
  code: string;
  status: string;
  label: string;
  colors: string[];
  regions: RegionSpec[];
  [key: string]: unknown;
};

/** The palette new observations and colours are taken from, in turn: DeepHealth's, then contrasts. */
export const PALETTE = [
  '#a10693',
  '#711766',
  '#e11d48',
  '#f59e0b',
  '#10b981',
  '#0ea5e9',
  '#6366f1',
  '#64748b',
];

export function nextColor(used: string[]): string {
  return (
    PALETTE.find(c => !used.map(u => u.toLowerCase()).includes(c)) ??
    PALETTE[used.length % PALETTE.length]
  );
}

/** The observations of the annotation YAML, as objects; none when it cannot be read. */
export function observationsOf(text: string): ObservationSpec[] {
  const { value, error } = readAnnotation(text);
  return !error && Array.isArray(value.observations)
    ? (value.observations as ObservationSpec[])
    : [];
}

/**
 * The annotation YAML with these observations: the other keys, and the comments around them, are kept —
 * only the observations are written again.
 */
export function withObservations(
  text: string,
  observations: ObservationSpec[]
): string {
  const doc = YAML.parseDocument(text || '{}');
  if (!doc.contents || !YAML.isMap(doc.contents)) {
    doc.contents = doc.createNode({});
  }
  doc.set('observations', doc.createNode(observations));
  return doc.toString({ lineWidth: 0 });
}

export function blankObservation(used: string[]): ObservationSpec {
  return {
    code: '',
    status: 'POSITIVE',
    label: '',
    colors: [nextColor(used)],
    regions: [{ tool: { name: TOOLS[0].value } }],
  };
}

/** The questions a region asks: the properties of its characterisation's JSON schema. */
export function questionsOf(region: RegionSpec): string[] {
  const properties = (
    region.characterisation?.json as { properties?: object } | undefined
  )?.properties;
  return properties && typeof properties === 'object'
    ? Object.keys(properties)
    : [];
}

const COLOR = /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/;

/**
 * What viewer-api would refuse in each observation (ObservationSpecification's constraints, the unique
 * code and status, the tool's control among the region's questions): one list per observation.
 */
export function observationProblems(
  observations: ObservationSpec[]
): string[][] {
  const seen = new Map<string, number>();
  observations.forEach((o, i) => {
    const key = `${o.code}|${o.status}`;
    if (!seen.has(key)) seen.set(key, i);
  });
  return observations.map((o, i) => {
    const problems: string[] = [];
    if (!String(o.code ?? '').trim()) problems.push('A code is needed.');
    if (!OBSERVATION_STATUSES.includes(o.status))
      problems.push(
        `Status must be one of ${OBSERVATION_STATUSES.join(', ')}.`
      );
    if (!String(o.label ?? '').trim()) problems.push('A label is needed.');
    const colors = Array.isArray(o.colors) ? o.colors : [];
    if (colors.length === 0) problems.push('At least one colour.');
    colors
      .filter(c => !COLOR.test(String(c)))
      .forEach(c => problems.push(`Not a colour: ${c}`));
    const regions = Array.isArray(o.regions) ? o.regions : [];
    if (regions.length === 0) problems.push('At least one way to draw it.');
    regions.forEach((r, j) => {
      if (!r?.tool?.name) problems.push(`Region ${j + 1}: a tool is needed.`);
      const control = r?.tool?.control;
      if (control && !questionsOf(r).includes(control)) {
        problems.push(
          `Region ${j + 1}: "${control}" is not one of its questions.`
        );
      }
    });
    if (o.code && seen.get(`${o.code}|${o.status}`) !== i) {
      problems.push(
        `${o.code} ${o.status} is already observation ${(seen.get(`${o.code}|${o.status}`) ?? 0) + 1}.`
      );
    }
    return problems;
  });
}

/** The task document, in the order the data managers' YAML files use. */
export function toDocument(draft: TaskDraft): Record<string, unknown> {
  const annotation = readAnnotation(draft.annotation).value;
  const doc: Record<string, unknown> = { name: draft.name.trim() };
  if (draft.description.trim()) doc.description = draft.description.trim();
  if (draft.instructions.trim()) doc.instructions = draft.instructions;
  if (draft.dataset.trim()) doc.bigQueryTableId = draft.dataset.trim();
  if (draft.initialPassesTable.trim())
    doc.bigQueryInitialPassesTableId = draft.initialPassesTable.trim();
  doc.requiredLabelers = draft.requiredLabelers;
  if (draft.logoutMinutes.trim())
    doc.logout = `PT${Number(draft.logoutMinutes)}M`;
  if (draft.reportsHidden) doc.reportsHidden = true;
  doc.taskers = draft.labellers;
  if (draft.reviewers.length) {
    doc.reviewConfiguration = {
      reviewers: draft.reviewers,
      casesPerReview: draft.casesPerReview,
    };
  }
  if (draft.predictions.enabled) {
    doc.predictionsConfiguration = {
      bigQueryPredictionTableId: draft.predictions.table.trim(),
      minConfidence: draft.predictions.minConfidence,
      maxConfidence: draft.predictions.maxConfidence,
      boxesColor: draft.predictions.boxesColor,
      ...(draft.predictions.cleanable ? { cleanable: true } : {}),
    };
  }
  ANNOTATION_KEYS.forEach(key => {
    if (annotation[key] != null) doc[key] = annotation[key];
  });
  return doc;
}

export function toYaml(draft: TaskDraft): string {
  return YAML.stringify(toDocument(draft), { lineWidth: 0 });
}

/** viewer-api's BigQueryTableValidator: project.dataset.table, the project lower case, 6 to 30 long. */
export const TABLE_PATTERN =
  /^[a-z0-9-]{6,30}\.[a-zA-Z0-9_]{1,1024}\..{1,1024}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type DraftErrors = Partial<
  Record<
    | 'name'
    | 'dataset'
    | 'initialPassesTable'
    | 'labellers'
    | 'reviewers'
    | 'requiredLabelers'
    | 'predictions'
    | 'annotation'
    | 'logout',
    string
  >
>;

/** What viewer-api would refuse, said before sending it. */
export function validate(draft: TaskDraft): DraftErrors {
  const errors: DraftErrors = {};
  if (!draft.name.trim()) errors.name = 'A task needs a name.';
  if (!draft.dataset.trim() && !draft.initialPassesTable.trim()) {
    errors.dataset =
      'Give the dataset table, or the table of earlier passes it starts from.';
  } else if (
    draft.dataset.trim() &&
    !TABLE_PATTERN.test(draft.dataset.trim())
  ) {
    errors.dataset = 'Expected project.dataset.table.';
  }
  if (
    draft.initialPassesTable.trim() &&
    !TABLE_PATTERN.test(draft.initialPassesTable.trim())
  ) {
    errors.initialPassesTable = 'Expected project.dataset.table.';
  }
  const badLabeller = draft.labellers.find(e => !EMAIL.test(e));
  if (badLabeller) errors.labellers = `Not an e-mail: ${badLabeller}`;
  const badReviewer = draft.reviewers.find(e => !EMAIL.test(e));
  if (badReviewer) errors.reviewers = `Not an e-mail: ${badReviewer}`;
  if (!(draft.requiredLabelers >= 1)) {
    errors.requiredLabelers = 'At least one labeller per item.';
  } else if (
    draft.labellers.length > 0 &&
    draft.requiredLabelers > draft.labellers.length
  ) {
    errors.requiredLabelers = `More labellers per item than labellers (${draft.labellers.length}).`;
  }
  if (draft.logoutMinutes.trim() && !(Number(draft.logoutMinutes) > 0)) {
    errors.logout = 'Minutes, more than 0.';
  }
  if (draft.predictions.enabled) {
    const p = draft.predictions;
    if (!TABLE_PATTERN.test(p.table.trim()))
      errors.predictions = 'The predictions table: project.dataset.table.';
    else if (
      !(
        p.minConfidence >= 0 &&
        p.maxConfidence <= 100 &&
        p.minConfidence <= p.maxConfidence
      )
    ) {
      errors.predictions =
        'Confidence between 0 and 100, the minimum below the maximum.';
    }
  }
  const annotation = readAnnotation(draft.annotation);
  if (annotation.error) errors.annotation = annotation.error;
  else if (annotation.observations.length === 0)
    errors.annotation = 'At least one observation.';
  else {
    const problems = observationProblems(observationsOf(draft.annotation));
    const at = problems.findIndex(p => p.length > 0);
    if (at >= 0)
      errors.annotation = `Observation ${at + 1}: ${problems[at][0]}`;
  }
  return errors;
}

/** Comma, semicolon, space or line separated e-mails, as pasted from a sheet or a mail. */
export function splitEmails(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map(e => e.trim().toLowerCase())
    .filter(Boolean);
}
